// Plan 08-06 — Tutorial runner (CLIENT-ONLY, DEC-021 exception).
//
// This module drives a scripted Apprentice match entirely on the client. It
// is the SINGLE intentional exception to DEC-021's server-authoritative state
// rule (CONTEXT line 88). The grep gate in runner.test.ts case 6 enforces
// that this file imports ZERO modules from `apps/web/src/net/*` or
// `apps/web/src/stores/auth*` — the tutorial NEVER reaches a real Match DO.
//
// Why: REQ-onboarding-tutorial mandates ≤10 minutes from install to the
// player's first real-opponent turn submission. Auth-then-tutorial would
// blow the budget; auth happens AFTER tutorial via the soft signup prompt.
//
// PlayerView shape mirrors backend's wire schema (apps/backend/src/match/
// view/player-view.ts via apps/web/src/game/PlayerViewSchema.ts) so the
// tutorial canvas can render through the same MatchCanvas + 5-layer scene
// graph that real matches use (Plan 04).

import type { PlayerView } from '../PlayerViewSchema'
import {
  APPRENTICE_SEED_RUNES,
  TUTORIAL_SCRIPT,
  TUTORIAL_SEED_RUNES,
} from './script'
import type { TutorialOutcome, TutorialStep } from './types'

// Re-export the deck seed so the test can pin it without reaching into script.ts
export { TUTORIAL_DECK_SEED } from './script'

export interface TutorialRunnerOpts {
  onPlayerView: (view: PlayerView) => void
  onComplete: () => void
  onCoachHint: (step: TutorialStep) => void
}

export interface TutorialRunner {
  /** Current PlayerView snapshot (always slot 0). */
  getView: () => PlayerView
  /** Advance one script step. Idempotent past completion. */
  advance: () => void
  /**
   * ANCHOR-03 (P2 canvas-driven) — brand the player's chosen cell as their
   * Anchor (tint 'a', is_anchor, anchor_owner 0) and emit the new view. The
   * canvas-driven tutorial routes the real AnchorPlacementOverlay / MatchGrid
   * placement here (in place of the network POST) on the place_anchor step.
   * Forgiving: a no-op if the cell is not a legal anchor target (is_anchor
   * false ∧ is_hollow false ∧ letter ≠ null). Does NOT advance the script.
   */
  placeAnchorAt: (cellIdx: number) => void
  /**
   * P5 (canvas-driven) — award the first trace's +1 token + record the word, on
   * the player's SUBMIT (the trace_word step itself is a no-op signpost). Tied
   * to the real submit so the board's token HUD reads 0 until the player traces.
   * Idempotent (a no-op once a token is banked). Does NOT advance the script.
   */
  awardTraceWord: (word: string) => void
  /** The most recently emitted coach-hint step (or null). */
  getCurrentHint: () => TutorialStep | null
  /** Whether the tutorial has reached the end of the script. */
  isComplete: () => boolean
  /**
   * Match outcome. `'player-win'` after the final step (King capture);
   * `'in-progress'` otherwise.
   */
  getOutcome: () => TutorialOutcome
}

/**
 * Build the initial PlayerView before any script step has played. Mirrors
 * the wire shape PlayerViewSchema.ts pins. NEVER consults a global store.
 */
function makeInitialTutorialView(): PlayerView {
  return {
    matchId: 'tutorial-00000000-0000-4000-8000-000000000001',
    your_slot: 0,
    current_turn_idx: 1,
    current_player_slot: 0,
    your_tokens: 0,
    your_role: null,
    your_role_expires_turn: null,
    your_words_played: [],
    // POWERUP-01 — seed the casual-default 4 (GDD §5.8) so the tutorial's
    // power-up step renders a real loadout. Inline string literals typed
    // against PowerUpName — no net/* or stores/auth* import (DEC-021 lock).
    your_powerup_loadout: [
      'scriveners_quill',
      'tongue_tie',
      'knights_vow',
      'whisperers_promise',
    ],
    // Mark them all available so the reused PowerupTray renders them as live
    // cards (not the disabled/locked state) on the inspect-power-ups step.
    available_power_ups: [
      'scriveners_quill',
      'tongue_tie',
      'knights_vow',
      'whisperers_promise',
    ],
    your_powerups_used: {},
    opponent_public: {
      captures: 0,
      powerup_loadout: [],
      role_kind: null,
      role_expires_turn: null,
    },
    // Plan 30-04 refresh: Runespring unified shared field (36 cells), no
    // chess pieces. Tutorial pre-seeds the player's row (cells 0..5) with
    // the canonical TUTORIAL_SEED_RUNES values; all other cells are
    // null-letter common cells (the flop adds runes at T6 per the script).
    board: Array.from({ length: 36 }, (_, idx) => {
      const seed =
        idx < TUTORIAL_SEED_RUNES.length ? TUTORIAL_SEED_RUNES[idx] : null
      return {
        cell_idx: idx,
        letter: seed ?? null,
        tier: 'common' as const,
        tint: seed !== undefined && seed !== null ? ('a' as const) : null,
        is_anchor: false,
        is_hollow: false,
        anchor_owner: null,
        piece_owner_slot: null,
        piece_kind: null,
        role_kind: null,
        role_expires_turn: null,
        role_extensions_used: 0,
        can_extend: false,
      }
    }),
    config: {
      language: 'en',
      minWordLength: 3,
      lengthCap: 30,
      turnClockMs: 30_000,
      powerupLoadout: [
        'scriveners_quill',
        'tongue_tie',
        'knights_vow',
        'whisperers_promise',
      ],
      dictionary_version: 'tutorial-v1',
    },
    role_costs: {
      knight: { cost: 1, min_word_length: 3 },
      bishop: { cost: 1, min_word_length: 3 },
      rook: { cost: 2, min_word_length: 4 },
      queen: { cost: 3, min_word_length: 5 },
    },
    // Phase 31 — caller-only fields added by Plan 31-01 schema extension.
    // The tutorial runs without affinity/anchor mechanics; pre-seeded with a
    // deterministic affinity tuple so consumers of the schema can render
    // without crashing. The runner does not exercise these surfaces in v1.
    your_affinity_letters: ['A', 'E', 'I', 'O'] as const,
    your_read_revealed_letters: [] as const,
    requires_anchor_placement: false,
    turn_phase: 'word',
    your_legal_moves: {},
  }
}

/**
 * Apply a single script step to the current PlayerView, returning the new
 * snapshot. Pure — does not touch any external state.
 */
function applyStep(prev: PlayerView, step: TutorialStep): PlayerView {
  const next: PlayerView = {
    ...prev,
    // Always advance to the step's turn_idx (it monotonically increases).
    current_turn_idx: step.turn_idx,
    // Player turns put slot 0 to play; apprentice turns put slot 1 to play.
    current_player_slot:
      step.kind === 'apprentice_turn'
        ? 1
        : step.kind === 'player_turn'
          ? 0
          : prev.current_player_slot,
  }

  if (step.kind === 'reveal' && step.reveal === 't1') {
    // T1 READY — letters were already dealt by makeInitialTutorialView.
    // Nothing new to reveal on the shared board yet.
    return next
  }

  if (step.kind === 'reveal' && step.reveal === 't6') {
    // T6 THE FLOP — GROW the field 36→48 (P3 canvas-driven), reproducing the
    // backend's "deepen-the-middle" contract (apps/backend turn.ts §5f.5): insert
    // 12 new cells at the center window [18,30) and shift the upper half outward
    // (every cell_idx ≥ 18 → +12). Cells 0..17 are untouched. Reproducing this
    // EXACT shift is what keeps the frontend's centerGrowWindow + finalCellKey
    // spawn/glide keys aligned, so the reused LetterLayer cascades the new runes
    // in and glides survivors (a naive tail-append would teleport them).
    //
    // The 12 new center cells carry a small flop word (BAT / ORE) so the grow
    // visibly deals fresh runes; an already-placed Anchor on an old cell_idx ≥ 18
    // (e.g. the Apprentice's at 26) rides the +12 shift with its state intact.
    const FLOP_RUNES: Record<number, string> = {
      18: 'B',
      19: 'A',
      20: 'T',
      24: 'O',
      25: 'R',
      26: 'E',
    }
    const makeFlopCell = (cellIdx: number): PlayerView['board'][number] => ({
      cell_idx: cellIdx,
      letter: FLOP_RUNES[cellIdx] ?? null,
      tier: 'common',
      tint: null,
      is_anchor: false,
      is_hollow: false,
      anchor_owner: null,
      piece_owner_slot: null,
      piece_kind: null,
      role_kind: null,
      role_expires_turn: null,
      role_extensions_used: 0,
      can_extend: false,
    })
    const grown = [
      ...next.board.slice(0, 18).map((c) => ({ ...c })),
      ...Array.from({ length: 12 }, (_, k) => makeFlopCell(18 + k)),
      ...next.board.slice(18).map((c) => ({ ...c, cell_idx: c.cell_idx + 12 })),
    ]
    return { ...next, board: grown }
  }

  if (step.kind === 'apprentice_turn' && step.apprentice_action) {
    const action = step.apprentice_action
    // Plan 30-04 refresh: Runespring has no chess pieces. The Apprentice's
    // "move" in the tutorial script becomes a tint flip on the destination
    // cell (Player B claims it as an Anchor via the new anchor placement
    // model — slot 1 = Player B = tint 'b' + is_anchor). Source cell tint
    // clears back to null (becomes a common unclaimed cell).
    const fromIdx = action.phase_b.from
    const toIdx = action.phase_b.to
    const newBoard = next.board.map((cell) => {
      if (cell.cell_idx === toIdx) {
        return {
          ...cell,
          tint: 'b' as const,
          is_anchor: true,
          anchor_owner: 1 as const,
        }
      }
      if (cell.cell_idx === fromIdx) {
        return {
          ...cell,
          tint: null,
          is_anchor: false,
          anchor_owner: null,
        }
      }
      return cell
    })
    return { ...next, board: newBoard }
  }

  if (step.kind === 'player_turn') {
    // First player turn (T1): the player traces a word → award 1 token,
    // P5 — the trace_word step is now a no-op signpost: the +1 token is awarded
    // INTERACTIVELY on the player's submit via `runner.awardTraceWord` (below),
    // so the board's token HUD reads 0 until they trace (was pre-awarded here in
    // auto-land, reading 1 before the trace). Token-award is out-of-band (not a
    // cursor advance) so the apply-then-show cadence for the reveal/apprentice/
    // flop steps stays intact.
    if (step.expected_player_action === 'trace_word') {
      return next
    }
    // T4 first empower: spend 1 token, gain Knight role.
    if (step.expected_player_action === 'empower') {
      return {
        ...next,
        your_tokens: Math.max(0, next.your_tokens - 1),
        your_role: 'knight',
        your_role_expires_turn: next.current_turn_idx + 3,
      }
    }
    // T3 token_bank + T1 affinity hints: no state change — educate only.
    // (inspect_powerups, T5, also falls through to the `return next` below.)
    if (step.expected_player_action === 'tap_continue') {
      return next
    }
    // ANCHOR-03 (P2 canvas-driven) — placement is now INTERACTIVE: the real
    // AnchorPlacementOverlay / MatchGrid gate lets the player tap a cell, routed
    // to `runner.placeAnchorAt(cellIdx)` (below). The step itself is a no-op
    // signpost; it no longer hard-codes a cell-1 brand (that pre-empted the
    // player's choice and double-branded once placeAnchorAt landed).
    if (step.expected_player_action === 'place_anchor') {
      return next
    }
    // T6 capture: player consumes Apprentice's Anchor → Hollow. Tutorial
    // complete. Plan 30-04 refresh: under Runespring, "capture" becomes
    // anchor consumption (the cell flips to is_hollow=true, breaking
    // adjacency for further word traces — see UI-SPEC §2 z=7 Hollow).
    if (step.expected_player_action === 'capture') {
      const newBoard = next.board.map((cell) => {
        if (cell.anchor_owner === 1 && cell.is_anchor) {
          return {
            ...cell,
            letter: null,
            is_anchor: false,
            is_hollow: true,
          }
        }
        return cell
      })
      return { ...next, board: newBoard }
    }
  }

  return next
}

/**
 * Construct a tutorial runner that walks `TUTORIAL_SCRIPT` step-by-step.
 *
 * The runner is forgiving: `advance()` always pushes to the next script
 * step. Real player input handling (validation against
 * `expected_player_action`) is wired by `<TutorialMatchHost>` (Task 3).
 */
export function createTutorialRunner(opts: TutorialRunnerOpts): TutorialRunner {
  let cursor = 0
  let view: PlayerView = makeInitialTutorialView()
  let lastHint: TutorialStep | null = null
  // Avoid lint warnings if `APPRENTICE_SEED_RUNES` is not consumed
  // structurally — we touch it once for diagnostic completeness only.
  void APPRENTICE_SEED_RUNES

  const runner: TutorialRunner = {
    getView: () => view,
    getCurrentHint: () => lastHint,
    isComplete: () => cursor >= TUTORIAL_SCRIPT.length,
    getOutcome: () =>
      cursor >= TUTORIAL_SCRIPT.length ? 'player-win' : 'in-progress',
    placeAnchorAt: (cellIdx) => {
      const cell = view.board.find((c) => c.cell_idx === cellIdx)
      // Mirror the overlay's eligibility filter (is_anchor false ∧ is_hollow
      // false ∧ letter ≠ null). Ineligible taps are inert — the runner stays
      // forgiving and never emits a spurious view.
      if (
        cell === undefined ||
        cell.is_anchor ||
        cell.is_hollow ||
        cell.letter === null
      ) {
        return
      }
      view = {
        ...view,
        board: view.board.map((c) =>
          c.cell_idx === cellIdx
            ? {
                ...c,
                tint: 'a' as const,
                is_anchor: true,
                anchor_owner: 0 as const,
              }
            : c,
        ),
      }
      opts.onPlayerView(view)
    },
    awardTraceWord: (word) => {
      // Idempotent: the tutorial only banks the one opening token.
      if (view.your_tokens > 0) return
      view = {
        ...view,
        your_tokens: 1,
        your_words_played: [word.toUpperCase()],
      }
      opts.onPlayerView(view)
    },
    advance: () => {
      if (cursor >= TUTORIAL_SCRIPT.length) {
        opts.onComplete()
        return
      }
      const step = TUTORIAL_SCRIPT[cursor]
      if (!step) {
        cursor = TUTORIAL_SCRIPT.length
        opts.onComplete()
        return
      }
      view = applyStep(view, step)
      opts.onPlayerView(view)
      // TUT-07 — emit the hint for ANY step carrying a player_hint_key, so the
      // t6 reveal step's t6_ready signpost fires (was previously gated to
      // player_turn only, leaving t6_ready dead).
      if (step.player_hint_key) {
        lastHint = step
        opts.onCoachHint(step)
      }
      cursor += 1
      if (cursor >= TUTORIAL_SCRIPT.length) {
        opts.onComplete()
      }
    },
  }
  return runner
}
